import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import ProgressiveImage from 'src/common/components/ProgressiveImage';
import { LanguageKeys } from 'src/common/constants/languages';
import { frostedLogoMaskImageSx } from 'src/common/styles/frostedPanelStyles';
import { isSocialShareOgPreview } from 'src/helpers/socialShareOgLayout';
import { ILogoBlock } from 'src/interfaces/PageBlock';
import tttLogo from 'src/static/images/ttt-logo.png';

interface Props {
  block: ILogoBlock;
  lang: LanguageKeys;
  contentKey?: string;
  inHero?: boolean;
}

export default function LogoBlockContent({ block, lang, contentKey, inHero }: Props) {
  const skipFrostedOverlay = isSocialShareOgPreview(contentKey);
  const { t } = useTranslation();
  const src = block.imageUrl?.trim() || tttLogo;
  const alt = block.alt?.[lang]?.trim() || t('base.defaultTitle');
  const maxHeight = block.maxHeight ?? 200;

  const imageSx = {
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    maxHeight: { xs: Math.min(maxHeight, 140), sm: Math.min(maxHeight, 180), md: maxHeight },
    objectFit: 'contain',
    display: 'block',
    opacity: 0.8,
    filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.55))',
  } as const;

  return (
    <Typography component="h1" variant="h1" sx={{ m: 0, width: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          width: 'fit-content',
          maxWidth: '100%',
          mx: 'auto',
        }}
      >
        <ProgressiveImage
          src={src}
          alt={alt}
          priority={inHero}
          objectFit="contain"
          sx={imageSx}
        />
        {skipFrostedOverlay ? null : (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              ...frostedLogoMaskImageSx(src),
            }}
          />
        )}
      </Box>
    </Typography>
  );
}
